(() => {
  const roots = document.querySelectorAll('[data-unisource-gatherup-reviews]');
  if (!roots.length) return;

  const MAX_ATTEMPTS = 35;
  const RETRY_DELAY = 300;

  function cleanText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function toArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  function clampRating(value) {
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return 5;
    return Math.max(0, Math.min(5, number));
  }

  function starString(rating) {
    const rounded = Math.round(clampRating(rating));
    return '★★★★★'.slice(0, rounded) + '☆☆☆☆☆'.slice(0, 5 - rounded);
  }

  function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return cleanText(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function flattenJsonLd(node, output = []) {
    if (!node) return output;

    if (Array.isArray(node)) {
      node.forEach((item) => flattenJsonLd(item, output));
      return output;
    }

    if (typeof node !== 'object') return output;

    output.push(node);

    if (node['@graph']) flattenJsonLd(node['@graph'], output);
    if (node.review) flattenJsonLd(node.review, output);
    if (node.itemReviewed) flattenJsonLd(node.itemReviewed, output);
    if (node.aggregateRating) flattenJsonLd(node.aggregateRating, output);

    return output;
  }

  function parseJsonLdFromRoot(root) {
    const scripts = Array.from(root.querySelectorAll('script[type="application/ld+json"], script.json-ld-content'));
    const parsed = [];

    scripts.forEach((script) => {
      const raw = cleanText(script.textContent);
      if (!raw || raw === '{}') return;

      try {
        parsed.push(JSON.parse(raw));
      } catch (error) {
        // Some widgets write incomplete JSON before finishing. Ignore until next retry.
      }
    });

    return parsed;
  }

  function extractFromJsonLd(jsonLdItems) {
    const nodes = [];
    jsonLdItems.forEach((item) => flattenJsonLd(item, nodes));

    const reviews = [];
    let aggregate = null;

    nodes.forEach((node) => {
      const type = toArray(node['@type']).map((entry) => String(entry).toLowerCase());

      if (!aggregate && node.ratingValue && (node.reviewCount || node.ratingCount || type.includes('aggregaterating'))) {
        aggregate = {
          rating: clampRating(node.ratingValue),
          count: cleanText(node.reviewCount || node.ratingCount || ''),
        };
      }

      if (type.includes('review') || node.reviewBody || node.reviewRating) {
        const rating = node.reviewRating?.ratingValue || node.ratingValue || 5;
        const author = typeof node.author === 'object' ? node.author.name : node.author;
        const title = node.name || node.headline || 'Customer review';
        const body = node.reviewBody || node.description || '';

        if (cleanText(body) || cleanText(title)) {
          reviews.push({
            rating: clampRating(rating),
            title: cleanText(title),
            body: cleanText(body),
            author: cleanText(author || 'Verified customer'),
            date: formatDate(node.datePublished || node.dateCreated),
          });
        }
      }
    });

    return { aggregate, reviews };
  }

  function extractFromVisibleWidget(root) {
    const source = root.querySelector('[data-gatherup-source]');
    if (!source) return { aggregate: null, reviews: [] };

    const reviewNodes = Array.from(source.querySelectorAll('[itemprop="review"], [class*="review"], [class*="testimonial"]'))
      .filter((node) => cleanText(node.textContent).length > 40)
      .slice(0, 12);

    const reviews = reviewNodes.map((node) => {
      const ratingNode = node.querySelector('[itemprop="ratingValue"], [class*="star"], [class*="rating"]');
      const authorNode = node.querySelector('[itemprop="author"], [class*="author"], [class*="name"]');
      const bodyNode = node.querySelector('[itemprop="reviewBody"], [class*="body"], [class*="text"], p');
      const titleNode = node.querySelector('[itemprop="name"], h3, h4, strong');
      const dateNode = node.querySelector('[itemprop="datePublished"], time, [class*="date"]');
      const text = cleanText(bodyNode?.textContent || node.textContent);

      return {
        rating: clampRating(ratingNode?.getAttribute('content') || ratingNode?.textContent || 5),
        title: cleanText(titleNode?.textContent || 'Customer review'),
        body: text.slice(0, 260),
        author: cleanText(authorNode?.textContent || 'Verified customer'),
        date: formatDate(dateNode?.getAttribute('datetime') || dateNode?.textContent),
      };
    });

    return { aggregate: null, reviews };
  }

  function render(root, data) {
    const grid = root.querySelector('[data-reviews-grid]');
    const empty = root.querySelector('[data-reviews-empty]');
    const summary = root.querySelector('[data-reviews-summary]');
    const ratingEl = root.querySelector('[data-reviews-rating]');
    const starsEl = root.querySelector('[data-reviews-stars]');
    const countEl = root.querySelector('[data-reviews-count]');
    const maxReviews = Number.parseInt(root.dataset.maxReviews || '4', 10);
    const reviews = data.reviews.slice(0, Number.isFinite(maxReviews) ? maxReviews : 4);

    if (!grid) return false;

    if (!reviews.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return false;
    }

    if (empty) empty.hidden = true;

    if (summary && data.aggregate) {
      summary.hidden = false;
      if (ratingEl) ratingEl.textContent = data.aggregate.rating.toFixed(1).replace('.0', '.0');
      if (starsEl) {
        starsEl.textContent = starString(data.aggregate.rating);
        starsEl.setAttribute('aria-label', `${data.aggregate.rating} out of 5 stars`);
      }
      if (countEl) {
        countEl.textContent = data.aggregate.count
          ? `Based on ${data.aggregate.count} customer reviews`
          : 'Verified customer feedback';
      }
    } else if (summary) {
      summary.hidden = false;
      if (ratingEl) ratingEl.textContent = '5.0';
      if (starsEl) starsEl.textContent = '★★★★★';
      if (countEl) countEl.textContent = 'Verified customer feedback';
    }

    grid.innerHTML = reviews.map((review) => {
      const title = cleanText(review.title || 'Customer review');
      const body = cleanText(review.body || 'Great service and support from the Unisource Apparel team.');
      const author = cleanText(review.author || 'Verified customer');
      const date = cleanText(review.date || '');
      const stars = starString(review.rating || 5);

      return `
        <article class="unisource-reviews__card">
          <div class="unisource-reviews__stars" aria-label="${review.rating || 5} out of 5 stars">${stars}</div>
          <strong>${escapeHtml(title)}</strong>
          <p>${escapeHtml(body)}</p>
          <div class="unisource-reviews__meta">
            ${escapeHtml(author)}
            ${date ? `<span class="unisource-reviews__date">${escapeHtml(date)}</span>` : ''}
          </div>
        </article>
      `;
    }).join('');

    return true;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function initRoot(root) {
    if (root.dataset.reviewsInitialized === 'true') return;
    root.dataset.reviewsInitialized = 'true';

    let attempts = 0;

    function tryRender() {
      attempts += 1;

      const jsonData = extractFromJsonLd(parseJsonLdFromRoot(root));
      const domData = extractFromVisibleWidget(root);
      const data = {
        aggregate: jsonData.aggregate || domData.aggregate,
        reviews: jsonData.reviews.length ? jsonData.reviews : domData.reviews,
      };

      if (render(root, data)) return;

      if (attempts < MAX_ATTEMPTS) {
        window.setTimeout(tryRender, RETRY_DELAY);
      }
    }

    tryRender();

    const source = root.querySelector('[data-gatherup-source]');
    if (source) {
      const observer = new MutationObserver(() => tryRender());
      observer.observe(source, { childList: true, subtree: true, characterData: true });
    }
  }

  roots.forEach(initRoot);
})();